import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Keyboard, 
  Heart, 
  FolderOpen, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft,
  Check
} from "lucide-react";

const ONBOARDING_KEY = "obyect_onboarding_complete_v1";

interface OnboardingModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

const steps = [
  {
    id: "welcome",
    title: "Welcome to Obyect",
    subtitle: "Your ad inspiration library",
    content: (
      <div className="space-y-4 text-center">
        <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10">
          <img src="/lovable-uploads/obyect_new.svg" alt="Obyect" className="h-12 w-auto" />
        </div>
        <p className="text-white/60">
          Discover winning ad creatives, save your favorites, and request custom designs for your brand.
        </p>
      </div>
    ),
  },
  {
    id: "shortcuts",
    title: "Keyboard Shortcuts",
    subtitle: "Navigate like a pro",
    content: (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "F", label: "Toggle favorite" },
            { key: "C", label: "Copy ad link" },
            { key: "R", label: "Request inspiration" },
            { key: "←  →", label: "Navigate ads" },
            { key: "Esc", label: "Close modal" },
          ].map(({ key, label }) => (
            <div 
              key={key}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
            >
              <kbd className="px-2.5 py-1.5 text-xs font-semibold bg-white/10 text-white rounded-lg border border-white/20 shadow-sm min-w-[40px] text-center">
                {key}
              </kbd>
              <span className="text-sm text-white/70">{label}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/40 text-center pt-2">
          Shortcuts work in the ad detail view
        </p>
      </div>
    ),
  },
  {
    id: "features",
    title: "Key Features",
    subtitle: "Make the most of Obyect",
    content: (
      <div className="space-y-4">
        {[
          {
            icon: Heart,
            title: "Save Favorites",
            desc: "Click the heart to save ads you love",
          },
          {
            icon: FolderOpen,
            title: "Organize in Boards",
            desc: "Group ads by project or campaign",
          },
          {
            icon: Sparkles,
            title: "Request Designs",
            desc: "Get custom creatives inspired by any ad",
          },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="flex items-start gap-4 p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="rounded-lg p-2" style={{ background: 'linear-gradient(180deg, rgba(112,186,255,0.2) 0%, rgba(37,139,255,0.2) 100%)' }}>
              <Icon className="h-5 w-5 text-[#70BAFF]" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-white">{title}</h4>
              <p className="text-xs text-white/60">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

export function OnboardingModal({ forceOpen, onClose }: OnboardingModalProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStep(0);
      return;
    }

    // Check if user has completed onboarding
    try {
      const completed = localStorage.getItem(ONBOARDING_KEY);
      if (!completed) {
        // Delay slightly for smoother UX
        const timer = setTimeout(() => setOpen(true), 500);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage not available
    }
  }, [forceOpen]);

  const handleClose = () => {
    setOpen(false);
    try {
      localStorage.setItem(ONBOARDING_KEY, "true");
    } catch {
      // ignore
    }
    onClose?.();
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md bg-[#13171c]/95 backdrop-blur-xl border-white/10 p-0 overflow-hidden text-white">
        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pt-6">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === step 
                  ? "bg-[#258BFF] w-6" 
                  : "bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          <div className="text-center space-y-1">
            <h2 className="text-xl font-semibold text-white">{currentStep.title}</h2>
            <p className="text-sm text-white/60">{currentStep.subtitle}</p>
          </div>
          
          <div className="py-2">
            {currentStep.content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white/50 hover:text-white hover:bg-white/10"
          >
            Skip
          </Button>
          
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrev}
                className="h-9 w-9 text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <button
              onClick={handleNext}
              className="px-5 py-2 text-sm font-medium text-white rounded-[13.417px] shadow-[0_4px_14px_0_rgba(37,139,255,0.39)] hover:opacity-90 active:scale-[0.98] transition-all flex items-center gap-2"
              style={{
                background: 'linear-gradient(180deg, #70BAFF 0%, #258BFF 100%)',
              }}
            >
              {isLastStep ? (
                <>
                  Get Started
                  <Check className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to reset onboarding (for testing or help button)
export function resetOnboarding() {
  try {
    localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    // ignore
  }
}
